<template>
  <div class="flsa-panel w-100">
    <FlsaPanelSkeleton v-if="panelLoading" />

    <div
      v-else
      class="flsa-panel-inner pb-6"
    >
      <HideRbac :permissions="['createFlsaSubmission']">
        <div class="flsa-panel-create px-6 pt-12">
          <div class="text-h1 text-textDarkest mb-4">
            Create a New FLSA Questionnaire
          </div>

          <p class="text-body-2 text-textDark mb-4">
            Select the jurisdiction to complete a questionnaire for this
            <span class="text-body-2 font-weight-medium text-textDarkest">
              {{ jobProfile?.title ?? 'job profile' }}
            </span>
            job profile.
          </p>

          <div class="mb-4">
            <label
              for="flsa-panel-jurisdiction"
              class="text-body-3 text-textDarkest d-block mb-1"
            >Jurisdiction</label>
            <v-select
              id="flsa-panel-jurisdiction"
              v-model="selectedFormUlid"
              :items="formSelectItems"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              rounded="lg"
              color="primary"
              menu-icon="$chevron-down"
              hide-details
              placeholder="Jurisdiction"
              class="flsa-panel-select text-body-1"
              :menu-props="{ contentClass: 'flsa-panel-select-menu' }"
            />
          </div>

          <v-btn
            block
            rounded="lg"
            color="primary"
            variant="flat"
            height="40"
            class="text-button-1"
            :disabled="!realFormUlids.has(selectedFormUlid)"
            @click="handleStartQuestionnaire"
          >
            Start FLSA Questionnaire
          </v-btn>
        </div>
      </HideRbac>

      <HideRbac :permissions="['listFlsaSubmissions']">
        <FlsaJurisdictionSubmissions
          :submissions="submissions"
        />
      </HideRbac>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import FlsaJurisdictionSubmissions from '@/components/jobProfiles/editor/FlsaJurisdictionSubmissions.vue';
import FlsaPanelSkeleton from '@/components/jobProfiles/editor/FlsaPanelSkeleton.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { getFlsaForms, getFlsaSubmissions } from '@/api/jobProfile';
import {
  attachFormYears,
  buildFlsaFormSelectItems,
  findEffectiveFlsaForm,
  getFlsaSubmissionProfileUlids,
  getRealFlsaFormUlids,
  navigateToFlsaFormCreate,
} from '@/helpers/flsa';
import { useTracking } from '@/stores/tracking/tracking';

const router = useRouter();

const { jobProfile, isHrisProfile } = storeToRefs(useCurrentJobProfile());

const panelLoading = ref(false);
const forms = ref([]);
const submissions = ref([]);
const selectedFormUlid = ref(null);

const realFormUlids = computed(() => getRealFlsaFormUlids(forms.value));

const formSelectItems = computed(() => buildFlsaFormSelectItems(forms.value));

const defaultFederalFormUlid = computed(() => (
  findEffectiveFlsaForm(forms.value, { jurisdiction: 'federal' })?.ulid || null
));

const loadPanelData = async () => {
  const profileUlids = getFlsaSubmissionProfileUlids({
    jobProfile: jobProfile.value,
    isHrisProfile: isHrisProfile.value,
  });

  const hasProfileContext = Boolean(
    profileUlids.jobProfileUlid || profileUlids.draftJobProfileUlid,
  );

  panelLoading.value = true;

  try {
    const formsData = await getFlsaForms();

    forms.value = formsData;

    if (!selectedFormUlid.value && defaultFederalFormUlid.value) {
      selectedFormUlid.value = defaultFederalFormUlid.value;
    }

    if (hasProfileContext) {
      const subsData = await getFlsaSubmissions({
        jobProfileUlid: profileUlids.jobProfileUlid || undefined,
        draftJobProfileUlid: profileUlids.draftJobProfileUlid || undefined,
      });

      submissions.value = attachFormYears(subsData.items, formsData);
    } else {
      submissions.value = [];
    }
  } catch (error) {
    forms.value = [];
    submissions.value = [];

    useAppSnackbar().addErrorSnackbar({
      text: `Failed to load FLSA data — ${error.message}`,
    });
  } finally {
    panelLoading.value = false;
  }
};

const handleStartQuestionnaire = () => {
  const selectedForm = forms.value.find((form) => form.ulid === selectedFormUlid.value);

  useTracking().flsaPanelStartedQuestionnaire({
    jurisdiction: selectedForm?.jurisdiction || 'federal',
  });

  const profileUlids = getFlsaSubmissionProfileUlids({
    jobProfile: jobProfile.value,
    isHrisProfile: isHrisProfile.value,
  });

  navigateToFlsaFormCreate({
    router,
    jobProfileUlid: profileUlids.jobProfileUlid,
    draftJobProfileUlid: profileUlids.draftJobProfileUlid,
    flsaFormUlid: selectedFormUlid.value,
    realFormUlids: realFormUlids.value,
  });
};

onMounted(() => {
  loadPanelData();
});

watch(
  () => [
    jobProfile.value?.ulid,
    isHrisProfile.value,
    jobProfile.value?.hris_reference_ulid,
  ],
  () => {
    selectedFormUlid.value = null;
    loadPanelData();
  },
);

watch(
  () => [forms.value, defaultFederalFormUlid.value],
  () => {
    if (!selectedFormUlid.value && defaultFederalFormUlid.value) {
      selectedFormUlid.value = defaultFederalFormUlid.value;
    }
  },
);
</script>

<style scoped lang="less">
.flsa-panel-inner {
  overflow-x: hidden;
}

.flsa-panel-select {
  :deep(.v-field__append-inner .v-icon) {
    color: @text-dark;
  }

  :deep(.v-field) {
    color: @text-darkest;
  }

  :deep(.v-select__selection--placeholder) {
    color: @text-dark;
  }
}
</style>

<style lang="less">
.flsa-panel-select-menu.v-overlay__content > .v-list {
  box-shadow: none;
  border: 1px solid @stroke-default;
  border-radius: 4px;
  padding: 0;
  background-color: @bg-default;
  min-width: 0;
}

.flsa-panel-select-menu .v-list-item {
  padding: 8px 12px;
  min-height: unset;
}

.flsa-panel-select-menu .v-list-item--disabled {
  opacity: 0.25;
}

.flsa-panel-select-menu .v-list-item-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}
</style>
